// AppShell — layout для всех экранов: Rail + Sidebar + Topbar + контент-Outlet,
// плюс глобальные хуки (⌘K поиск, ⌘\ скрыть sidebar, Esc закрыть оверлеи) и
// DnD-overlay при перетаскивании файлов в окно. Монтируется один раз через
// pathless layout-route (router/app-layout.route.tsx); смена URL внутри Outlet
// не пересоздаёт Shell — только содержимое. Это важно для sidebarOpen и DnD-
// счётчика (иначе теряли бы состояние при каждой навигации).

import { createSignal, onMount, onCleanup, createMemo } from 'solid-js'
import type { Component } from 'solid-js'
import { useNavigate, useRouterState, Outlet } from '@tanstack/solid-router'
import { Rail, Sidebar, Topbar } from './Shell'
import { SearchModal, DropOverlay } from './Views'
import { WELLS } from './data'
import { viewNavigate } from './router/links'

// Парсим текущий pathname в логический «view» — нужен для подсветки rail-пункта,
// breadcrumbs в Topbar, и активной скважины в Sidebar. Дублирует структуру
// дерева роутов, но снаружи дерева (в Shell) — намеренно: Shell не привязан к
// конкретным route-объектам, чтобы не делать циклические импорты shell↔routes.
type View =
  | 'dashboard' | 'wells' | 'well' | 'note'
  | 'files' | 'telemetry' | 'tasks' | 'tools'
  | 'states' | 'fields' | 'casings' | 'muds' | 'public_states'
  | 'knbk_assets' | 'settings'

function parseView(pathname: string): { view: View; wellId: string | null } {
  const base = '/app'
  const p = pathname.startsWith(base) ? pathname.slice(base.length) : pathname
  const seg = p.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean).map(decodeURIComponent)
  if (seg.length === 0) return { view: 'dashboard', wellId: null }
  switch (seg[0]) {
    case 'wells': {
      if (seg.length === 1) return { view: 'wells', wellId: null }
      const wellId = seg[1] ?? null
      if (seg[2] === 'note') return { view: 'note', wellId }
      return { view: 'well', wellId }
    }
    case 'files':         return { view: 'files',         wellId: null }
    case 'telemetry':     return { view: 'telemetry',     wellId: null }
    case 'tasks':         return { view: 'tasks',         wellId: null }
    case 'tools':         return { view: 'tools',         wellId: null }
    case 'fields':        return { view: 'fields',        wellId: null }
    case 'casings':       return { view: 'casings',       wellId: null }
    case 'muds':          return { view: 'muds',          wellId: null }
    case 'public_states': return { view: 'public_states', wellId: null }
    case 'states':        return { view: 'states',        wellId: null }
    case 'knbk-assets':   return { view: 'knbk_assets',   wellId: null }
    case 'settings':      return { view: 'settings',      wellId: null }
    default:              return { view: 'dashboard',     wellId: null }
  }
}

export const AppShell: Component = () => {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  const parsed = createMemo(() => parseView(pathname()))
  const view = (): View => parsed().view
  // Дефолт первой скважины — нужен Sidebar для выделения «активной» когда мы не
  // на /wells/$wellId. Это UI-only fallback, на роутинг не влияет.
  const wellId = (): string => parsed().wellId ?? WELLS[0]!.id

  const [sidebarOpen, setSidebarOpen] = createSignal(true)
  const [searchOpen, setSearchOpen] = createSignal(false)
  const [dropOpen, setDropOpen] = createSignal(false)
  // Drawer rail+sidebar для мобильных. На десктопе игнорируется (см. media-query
  // в layout.css). Закрываем при любой навигации, чтобы юзер не залипал в
  // открытом overlay'е после клика по элементу меню.
  const [mobileMenuOpen, setMobileMenuOpen] = createSignal(false)

  const goView = (v: string) => {
    navigate(viewNavigate(v))
    setMobileMenuOpen(false)
  }

  // Mock-дашборд скважины (/wells/$wellId) ретайрнут — Sidebar/Dashboard ведут
  // в реальный CRUD-каталог /wells. Конкретный well-id из мок-сайдбара тут
  // игнорируется (мок-id не соответствуют реальным записям).
  const openWell = (_id: string) => {
    navigate({ to: '/wells', search: {} })
    setMobileMenuOpen(false)
  }

  const currentWell = () => WELLS.find((w) => w.id === wellId())

  // На rail подсвечиваем dashboard когда мы внутри well/note (это сводка
  // основного контекста). Иначе — текущий view сам по себе.
  const railActive = () => {
    const v = view()
    return v === 'well' || v === 'note' ? 'dashboard' : v
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
      if (meta && e.key === '\\') {
        e.preventDefault()
        setSidebarOpen((o) => !o)
      }
      if (e.key === 'Escape') {
        setSearchOpen(false)
        setDropOpen(false)
        setMobileMenuOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  onMount(() => {
    let counter = 0
    const onEnter = (e: DragEvent) => {
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files')) {
        counter++
        setDropOpen(true)
      }
    }
    const onLeave = () => {
      counter = Math.max(0, counter - 1)
      if (counter === 0) setDropOpen(false)
    }
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      counter = 0
    }
    const onOver = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragenter', onEnter)
    window.addEventListener('dragleave', onLeave)
    window.addEventListener('drop', onDrop)
    window.addEventListener('dragover', onOver)
    onCleanup(() => {
      window.removeEventListener('dragenter', onEnter)
      window.removeEventListener('dragleave', onLeave)
      window.removeEventListener('drop', onDrop)
      window.removeEventListener('dragover', onOver)
    })
  })

  return (
    <div
      class={
        'app'
        + (sidebarOpen() ? '' : ' sidebar-collapsed')
        + (mobileMenuOpen() ? ' mobile-menu-open' : '')
      }
      data-screen-label={view()}
    >
      <Rail
        active={railActive()}
        onChange={(v) => goView(v)}
        onSearch={() => { setSearchOpen(true); setMobileMenuOpen(false) }}
        onSettings={() => goView('settings')}
        onToggleSidebar={() => setSidebarOpen((o) => !o)}
      />
      <Sidebar
        activeWell={wellId()}
        currentView={view()}
        onOpenWell={openWell}
        onOpenView={goView}
        onOpenSettings={() => goView('settings')}
      />
      {/* Backdrop виден только на мобиле, когда drawer открыт. Клик закрывает. */}
      <div
        class="mobile-backdrop"
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />
      <div class="main">
        <Topbar
          view={view()}
          well={currentWell()}
          onOpenView={goView}
          onOpenSearch={() => setSearchOpen(true)}
          onToggleMobileMenu={() => setMobileMenuOpen((o) => !o)}
        />
        <div class="content">
          <Outlet />
        </div>
      </div>

      <SearchModal visible={searchOpen()} onClose={() => setSearchOpen(false)} />
      <DropOverlay visible={dropOpen()} onClose={() => setDropOpen(false)} />
    </div>
  )
}
